import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Form, InputNumber, Row, Col } from 'antd';
import Icon from '@ant-design/icons';
import styles from './AddAmountModal.module.less';
import Modal from '../Modal/Modal';
import { Button } from '../Button/Button';
import LockIcon from '../../assets/img/blueLockIcon.svg?react';
import { CreateWalletInvoiceRequest, WalletInvoiceResponse } from '../../api/models/payments';
import { useHistory } from 'react-router-dom';
import {
  addAmountScreenCancelClicked,
  addAmountScreenNextClicked,
  addAmountScreenOpen
} from '../../utils/analytika/events';
import useAnalytika from '../../utils/analytika/reporterAnalytika';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../types/store';
import { userSelector } from '../../redux/selectors/user';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';
import { ANALYTIKA_EVENTS_V2 } from '../../utils/analytika/enums';

interface AddAmountModalProps {
  visible: boolean;
  onClose: () => void;
  currency?: string;
  createWalletInvoice?: (request: CreateWalletInvoiceRequest) => Promise<WalletInvoiceResponse>;
}

const AddAmountModal = ({ visible, onClose }: AddAmountModalProps) => {
  const { t } = useTranslation();
  const [isFormSending] = useState(false);
  const [amount, setAmount] = useState<number>(0);
  const history = useHistory();

  const {
    userWalletConfig: {
      userBalance: { currency }
    }
  } = useSelector(userSelector);

  const {
    invoices: { createWalletInvoice }
  } = useDispatch<AppDispatch>();

  const { fireEvent } = useAnalytika();

  useEffect(() => {
    visible && fireEvent(addAmountScreenOpen());
  }, [visible]);

  const handleCancel = () => {
    fireEvent(addAmountScreenCancelClicked({ amount_text: amount }));
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_CANCEL_WALLET,
      event_version: 1,
      wallet_balance: amount,
      wallet_currency: currency
    });
    onClose();
  };

  const handleAmountChange = (amount: number | null) => {
    setAmount(amount || 0);
  };

  const handleSubmit = async () => {
    if (!amount) return;
    try {
      const { id } = await createWalletInvoice({ amount, currency });
      fireEvent(addAmountScreenNextClicked({ amount_text: amount, transaction_id: id }));
      publishAV2Event({ event_name: ANALYTIKA_EVENTS_V2.EXPR_NEXT_ADD_CREDIT, event_version: 1, amount, currency });
      history.push(`/invoices/${id}`);
      return null;
    } catch (err) {
      // eslint-disable-next-line no-console
      // TODO: handle errors
    }
  };
  return (
    <Modal
      width={483}
      title={t('modal.addAmount.title')}
      visible={visible}
      onClose={onClose}
      footer={[
        <Button
          key="back"
          size={'large'}
          disabled={isFormSending}
          onClick={handleCancel}
          data-testid="buttonCancelModal"
        >
          {t('general.cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          size={'large'}
          disabled={isFormSending || !amount || amount < 1}
          onClick={handleSubmit}
          data-testid="buttonSubmitModal"
        >
          {t('modal.addAmount.nextBtn')}
        </Button>
      ]}
    >
      <div className={styles.content}>
        <Row>
          <Col xs={24} md={24} lg={24}>
            <Form id="addAmountForm" data-testid="addAmountForm" layout="vertical" hideRequiredMark>
              <Row>
                <Col xs={24} md={24} lg={24}>
                  <Form.Item label={`${t('modal.addAmount.amountFieldTitle')} (${currency})`} data-testid="userIdItem">
                    <InputNumber
                      data-testid="amountInput"
                      size={'large'}
                      step={10}
                      min={0}
                      precision={0}
                      value={amount}
                      placeholder={t('modal.addAmount.placeholder')}
                      onChange={handleAmountChange}
                    />
                  </Form.Item>

                  <div className={styles.secureNotice}>
                    <Icon component={() => <LockIcon />} />
                    <Typography.Text className={styles.noticeText}>
                      {t('modal.addAmount.securityNotice')}
                    </Typography.Text>
                  </div>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default AddAmountModal;
